import BigNumber from 'bignumber.js'

import { CurrencyAddress, CurrencyModel } from '@root/entities/currency'
import { currencyRegistry } from '@root/entities/currency/atoms'
import { Network } from '@root/entities/network'
import { PairAddress } from '@root/entities/pair'
import { WalletAddress } from '@root/entities/wallet'

import { TxHash } from './tx-hash'
import { TxId } from './tx-id'

import type { UserApi } from '@root/shared/api'
import type { TokenLogosHost } from '@root/shared/models'

export type Type = UserApi.TransactionTypeDto
export type Status = UserApi.TransactionStatusDto

export const txStatuses: Array<Status> = ['failed', 'success', 'pending']
export const txTypes: Array<Type> = ['transfer', 'buy', 'sell', 'swap']
export type TxStatus = (typeof txStatuses)[number]
interface SwapInfo {
  currencyIn: CurrencyModel
  currencyOut: CurrencyModel
  currencyPriceNative: BigNumber
  currencyPriceUsd: BigNumber

  inAmount: BigNumber
  inAmountUsd: BigNumber
  outAmount: BigNumber
  outAmountUsd: BigNumber
  /**
   * wallet address
   */
  from: WalletAddress
}

export interface TransferInfo {
  currency: CurrencyModel
  value: BigNumber
  valueUsd: BigNumber
  from: WalletAddress
  to: WalletAddress
}

export interface Transaction {
  id: TxId
  createdAt: Date
  status: Status
  type: Type
  network: Network.Network

  swapInfo: SwapInfo | undefined
  transferInfo: TransferInfo | undefined

  hash: TxHash
  block: number
  nonce: number

  transactionFee: number
  gasPrice: number
  gasLimit: number
  inputData: string
  pairAddress: PairAddress | undefined
}

export const fromDto = (
  dto: UserApi.TransactionDto,
  imagesHost: TokenLogosHost,
): Transaction => {
  const mapSwapInfo = (): SwapInfo | undefined => {
    const swapInfo = dto.swapInfo
    if (!swapInfo) {
      return undefined
    }

    return {
      inAmount: BigNumber(swapInfo.amountIn),
      inAmountUsd: BigNumber(swapInfo.amountInUsd),
      outAmount: BigNumber(swapInfo.amountOut),
      outAmountUsd: BigNumber(swapInfo.amountOutUsd),
      currencyIn: currencyRegistry.getOrCreate(
        CurrencyAddress(swapInfo.tokenIn.address),
        Network.fromDto(swapInfo.tokenIn.network),
        () => CurrencyModel.fromTokenMeta(swapInfo.tokenIn, imagesHost),
      ),
      currencyOut: currencyRegistry.getOrCreate(
        CurrencyAddress(swapInfo.tokenOut.address),
        Network.fromDto(swapInfo.tokenOut.network),
        () => CurrencyModel.fromTokenMeta(swapInfo.tokenOut, imagesHost),
      ),
      from: WalletAddress(swapInfo.from),
      currencyPriceNative: BigNumber(dto.tokenPriceInNative),
      currencyPriceUsd: BigNumber(dto.tokenPriceInUsd),
    }
  }
  const mapTransferInfo = (): TransferInfo | undefined => {
    if (!dto.transferInfo) {
      return undefined
    }

    return {
      currency: CurrencyModel.fromTokenMeta(dto.transferInfo.token, imagesHost),
      from: WalletAddress(dto.transferInfo.from),
      to: WalletAddress(dto.transferInfo.to),
      value: BigNumber(dto.transferInfo.value),
      valueUsd: BigNumber(dto.transferInfo.valueUsd),
    }
  }

  return {
    id: TxId(dto.id),
    createdAt: new Date(dto.createdAt),
    block: dto.block,
    gasLimit: dto.gasLimit,
    gasPrice: dto.gasPrice,
    inputData: dto.inputData,
    network: Network.fromDto(dto.network),
    nonce: dto.nonce,
    swapInfo: mapSwapInfo(),
    transferInfo: mapTransferInfo(),
    hash: TxHash(dto.txHash),
    status: dto.status,
    type: dto.type,
    transactionFee: dto.transactionFee,
    pairAddress: dto.pairAddress ? PairAddress(dto.pairAddress) : undefined,
  }
}

export const totalAmountNative = (tx: Transaction) => {
  if (!tx.swapInfo) {
    return BigNumber(0)
  }

  return tx.type === 'buy' ? tx.swapInfo.inAmount : tx.swapInfo.outAmount
}

export const totalAmountUsd = (tx: Transaction) => {
  if (!tx.swapInfo) {
    return BigNumber(0)
  }

  return tx.type === 'buy' ? tx.swapInfo.inAmountUsd : tx.swapInfo.outAmountUsd
}

export const getSwapQuoteCurrency = (tx: Transaction) => {
  if (!tx.swapInfo) {
    return undefined
  }

  return tx.type === 'buy' ? tx.swapInfo.currencyOut : tx.swapInfo.currencyIn
}
